// Apps dropdown body — rendered inside the @symbo.ls/dropdown panel
// when the global search gains focus or hover (see
// functions/searchApps.js openSearchAppsDropdown). iOS-springboard
// layout (2026-06-06 per Nika), divided by category sections (Work /
// Team / Insights — SEARCH_APP_GROUPS): calm glyph tiles (AppGlyphTile)
// with labels beneath. The panel itself carries the dashboard-card
// surface (background 'document' + round 'C' via the dropdown
// plugin's layer overrides).
//
// The enter/leave handlers keep the hover-open path's 200ms
// close-debounce alive while the cursor is over the panel (same
// bridge pattern as AppNavbarMoreMenu).

import { searchAppGroupsFor } from '../functions/searchApps.js'

// THE house keyboard model for every dropdown menu (shared/functions/
// menuKeyboard.js) — arrow cycle, focus-in-on-open, focus-back-on-close — is
// reached with `el.call('<name>')` here. It is NEVER imported.
//
// It used to be a top-level import dropped into `scope` (`focusIn:
// armMenuFocus`). That shape works locally and is dead on a published record.
// `@symbo-ls/*` normalizes to `@symbo.ls/*` and matches frank's
// EXTERNAL_PACKAGES list, so `stubExternalsPlugin` (smbls/plugins/frank/
// toJSON.js) swaps the module for a stub and `stringifyFunctions` collapses
// every slot that read from it to ''. Measured on the served UI-kit catalog
// (2026-09-20): `[DOMQL] Lifecycle "onRender" error: this.focusIn is not a
// function` and `[smbls] onRemove error: el.scope.focusBack is not a
// function`. Three more slots (navRows / nodeVisible / releaseFocus) carried
// the identical stub and escaped that report only because no keystroke ever
// reached the panel — the keyboard model was gone in full, not in part.
//
// `el.call` defers the lookup to the runtime-merged context, and `shared` is
// merged by every consumer of this panel (packages/workspace/
// sharedLibraries.js, packages/workspace-ui/sharedLibraries.js), so the five
// names resolve in the shell, in the catalog and on a published record alike.
// Same cure and same reason as the import header of
// tickets/components/TicketDetailModal.js.
//
// `searchAppGroupsFor` above stays an import on purpose: a declaration in
// THIS package is bundled inline and lifted into globalScope, which is the
// case frank does support. `__tests__/searchAppsMenuScope.test.js` revives
// every handler in a fresh realm and pins both halves — the stub that must
// not come back, and the lift that must keep working.

// Since 9A (DESIGN-WL-PAGESLOT-OPEN-SPLIT-SEARCH-1) this panel is THE ONE
// search dropdown: a filter row mirroring the header input, the app tiles on
// an empty query, and the typed results in the same panel (SamResults).
export const SearchAppsMenu = {
  // ── keyboard (A2-06) ────────────────────────────────────────────────────
  // The launcher was pointer-only: with the panel visibly open, Tab walked
  // the BACKGROUND page (org switcher → invite → dashboard cards) and a
  // keyboard user could never reach a tile. It now implements the house menu
  // contract (shared/functions/menuKeyboard.js) like every other dropdown:
  // `role="menu"` + `tabindex="-1"` on the panel, `role="menuitem"` on every
  // actionable row, arrow/Home/End cycling, Tab kept INSIDE while the panel
  // is up, Escape closing it and handing focus back.
  //
  // The ONE thing this panel does differently, and why: it opens BOTH from a
  // trigger (the header chevron) AND as a companion to the focused search
  // field. Pulling focus into the panel in the second case would blur the
  // input the user is about to type into — and, worse, that blur is what
  // arms `scheduleSearchAppsClose`, so an unconditional focus-on-open closed
  // the launcher it had just opened. `armIfOutside` therefore takes focus
  // only when the search field does NOT already hold it, which is exactly
  // the chevron/keyboard case the audit reported.
  //
  // `scope` carries ONLY what this file declares. Every shared helper is an
  // `el.call` — see the keyboard-model note at the top of this file.
  scope: {
    // Does the header search field hold focus right now? Its own onBlur owns
    // the launcher's close, so it must keep focus while it has it.
    searchHasFocus() {
      if (typeof document === 'undefined') return false
      const a = document.activeElement
      return !!(a && a.closest && a.closest('[data-key="HeaderSearch"]'))
    },

    // The panel node this body is mounted INTO. The dropdown plugin mounts a
    // layer body with `el.set(content)`, so the body's own key is `content` —
    // never `SearchAppsMenu`. The plugin's Panel marker is the stable handle.
    panelNode(el) {
      const n = el && el.node
      return (n && n.closest && n.closest('[data-smbls-dropdown="panel"]')) || n
    },

    armIfOutside(el) {
      if (this.searchHasFocus()) return
      el.call('armMenuFocus', el, {
        when: (node) => !this.searchHasFocus() && el.call('menuNodeVisible', node),
        // A TRIGGER-opened panel (`_launcherMode`, functions/searchApps.js)
        // puts the caret in its own filter field, so typing filters at once.
        // Not on a touch screen: there the focus would raise the keyboard
        // over the tiles; a tap on the field does that when it is wanted.
        ...(el.state &&
        el.state.root &&
        el.state.root._launcherMode === 'trigger' &&
        !el.call('isCoarsePointer')
          ? { selector: '[data-launcher-filter]' }
          : {})
      })
    },

    // Tab must not walk the page behind an open overlay. Cycle the same rows
    // the arrows do; wrap at both ends.
    tabCycle(e, el) {
      if (!e || e.key !== 'Tab') return false
      // The trigger mode's own filter field is a stop too (first in DOM
      // order); hidden in the companion, it drops out with getClientRects.
      const rows = [
        ...this.panelNode(el).querySelectorAll(
          '[data-launcher-filter], [role="menuitem"], [data-launcher-category]'
        )
      ].filter((n) => !n.hasAttribute('disabled') && n.getClientRects().length)
      if (!rows.length) return false
      const idx = rows.indexOf(typeof document !== 'undefined' ? document.activeElement : null)
      const next = e.shiftKey
        ? (idx <= 0 ? rows.length : idx) - 1
        : idx < 0
          ? 0
          : (idx + 1) % rows.length
      e.preventDefault()
      e.stopPropagation()
      rows[next].focus()
      return true
    },

    // EVERY close this component owns stamps the dismissal FIRST. The
    // plugin's closeDropdown restores focus to the layer's restoreEl —
    // this launcher's is the search INPUT (A2-08) — and the input's onFocus
    // opens the launcher, so an unstamped close re-opened the panel it had
    // just closed and Escape looked inert (measured live: layerId stayed
    // 'searchApps' through an Escape). The panel's own onRemove also stamps,
    // but DOMQL disposes the body AFTER closeDropdown has already moved
    // focus, so the stamp has to be here, at the call site.
    dismiss(el) {
      try {
        el.call('noteSearchAppsDismissed')
      } catch (_) {}
      try {
        el.call('closeDropdown', 'searchApps')
      } catch (_) {}
    },

    closeMenu(el) {
      this.dismiss(el)
      el.call('restoreMenuFocus', el)
    }
  },
  role: 'menu',
  tabindex: '-1',
  'aria-label': (el) => el.call('polyglot', 'shell.headerSearch.openDropdown'),
  onKeydown: (e, el, s) => {
    // Vertical first, then horizontal — the springboard is a grid, and both
    // axes walk the same DOM-ordered row list.
    if (el.call('menuArrowNav', e, el.node)) return
    if (el.call('menuArrowNav', e, el.node, { orientation: 'x' })) return
    if (el.scope.tabCycle(e, el)) return
    if (e.key === 'Escape') {
      e.preventDefault()
      e.stopPropagation()
      // A trigger-opened panel's own filter (SamFilterField): Escape clears
      // the typed text first and puts the caret back in the field, from the
      // field or from a tile; the next Escape closes the panel.
      if (s && s.root && s.root._launcherMode === 'trigger' && s.root._launcherFilter) {
        s.root.update({ _launcherFilter: '' }, { preventFetch: true })
        const field = el.SamFilterField && el.SamFilterField.AppInputBare
        if (field && field.node) field.node.focus()
        return
      }
      el.scope.closeMenu(el)
    }
  },
  // Plugin-hosted: this body is MOUNTED only while the layer is open, so the
  // mount IS the open edge and the unmount IS the close edge.
  onRemove: (el) => {
    // Stamp the dismissal BEFORE the plugin's focus restore lands on the
    // search input — that restore would otherwise re-open this very panel and
    // make Escape / outside-click look inert. See noteSearchAppsDismissed in
    // functions/searchApps.js.
    try {
      el.call('noteSearchAppsDismissed')
    } catch (_) {}
    // Typing closes the launcher while the search input still owns focus.
    // Cancel the menu's pending poll without taking the caret back to its
    // original trigger; an actual menu dismissal still restores focus.
    if (el.scope.searchHasFocus()) el.call('releaseMenuFocus', el)
    else el.call('restoreMenuFocus', el)
  },

  // House MenuPanel chrome — the one house dropdown surface, relayed, never
  // repainted here (Nika 2026-09-02: "keep them consistent and
  // default-relayed"); the springboard layout keeps its roomier padding/gap.
  extends: 'MenuPanel',
  // THE ONE GLASS RECIPE (dropdowns spec §1, defect D5) — Nika 2026-09-30 "make searchdropdon bg blur": the
  // dropdown's own VISIBLE glass (shared designSystem theme.js `search-glass`: the card-glass alpha, the house
  // blur), idle tiles and typed results alike. The recipe sits on the dropdown LAYER, not here
  // (functions/searchApps.js openSearchAppsDropdown passes theme + shadow + round to the plugin Panel): the
  // Panel is a backdrop root while it is open, so a backdrop-filter on this body sampled only the Panel's own
  // empty surface and the page showed sharp through the fill (measured live on dev 3efc1eace). This body
  // therefore paints NOTHING: MenuPanel's opaque `dialog` surface and its `overlay` elevation are dropped —
  // `theme: null` plus the transparency pair, the way AppHeader drops the navbar glass — and the keyboard
  // model, the role and the focus arming MenuPanel relays stay.
  theme: null,
  background: 'transparent',
  backdropFilter: 'none',
  shadow: 'none',
  // MenuPanel repaints its elevation when the panel itself holds focus (the brand reset would draw a ring on
  // the container instead). The elevation is the layer's now; the container still takes no ring. Same key as
  // MenuPanel's (`boxShadow`) — a second shadow prop beside it would leave the winner to emission order.
  ':focus-visible': { boxShadow: 'none' },
  // DESIGN_SPEC 6.5: under reduced transparency the glass popover is the opaque `dialog` surface — this body
  // covers the Panel edge to edge (same rect, same radius), so the layer's glass is never seen.
  '@reduceTransparency': { theme: 'dialog' },
  borderWidth: '0',
  tag: 'div',
  gap: 'A',
  padding: 'A1',
  // 9A (Nika 2026-09-28: "make it more wider and add maxWidth also … make it
  // a bit taller"): the brand's wide overlay, never wider than the panel the
  // viewport allows (functions/searchApps.js bounds the positioned panel);
  // three quarters of the viewport tall (the shell's `searchPanelMaxH`), and
  // the filter row and toolbar stay put while the tiles or the results
  // scroll INSIDE (SamSections / SamResults are the scrollers).
  width: 'overlayXl',
  dataLauncherRail: (el) => (el.railLauncher ? 'true' : null),
  // Beside a sidebar the launcher is a compact app directory. The same
  // tiles keep their routes, drag behavior and keyboard actions. Its width
  // is the brand's floating side panel (`overlayLg`, one step over the form
  // width it wore; Nika 2026-10-29: "little bit increase widtg of sidebar of
  // more apps"); functions/searchApps.js still bounds the positioned panel
  // by the room beside the rail.
  '&[data-launcher-rail]': {
    width: 'overlayLg',
    // Match the dropdown positioner's viewport gutters. The header stays
    // visible while SamSections / SamResults own the remaining scroll area.
    height: '100dvh',
    maxHeight: 'inherit',
    padding: 'A',
    gap: 'A'
  },
  '&[data-launcher-rail] [data-key="SamHeader"]': { justifyContent: 'flex-start', gap: 'Y' },
  '&[data-launcher-rail] [data-key="SamTitle"]': { flex: '1 0 100%', padding: '0 Z Y' },
  '&[data-launcher-rail] [data-key="SamSections"]': { gap: 'A', paddingBottom: 'A' },
  '&[data-launcher-rail] [data-key="SasGrid"]': {
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 'Y'
  },
  '&[data-launcher-rail] [data-launcher-tile]': {
    flow: 'x',
    justifyContent: 'flex-start',
    width: '100%',
    minWidth: '0',
    minHeight: 'controlH',
    padding: 'Y Z',
    gap: 'Z',
    round: 'radiusControl',
    gridColumn: 'auto'
  },
  '&[data-launcher-rail] [data-key="IconTile"]': {
    boxSize: 'glyphPlateSm',
    minWidth: 'glyphPlateSm',
    border: 'none',
    flexShrink: '0'
  },
  '&[data-launcher-rail] [data-key="IconTile"] [data-key="Icon"]': { boxSize: 'iconMd' },
  '&[data-launcher-rail] [data-launcher-tile] [data-key="Label"]': {
    textAlign: 'left',
    minWidth: '0',
    fontWeight: '400'
  },
  maxWidth: '100%',
  maxHeight: 'searchPanelMaxH',
  boxSizing: 'border-box',
  '@mobileL': {
    gap: 'A',
    padding: 'A'
  },

  // Re-entering the panel both cancels the pending close AND lifts the
  // dismiss-inert armed by scheduleSearchAppsClose (searchApps.js) — an
  // open, hovered panel must stay fully interactive so a tile click still
  // lands (setDropdownInert(false) restores the @symbo.ls/dropdown Panel's
  // pointer-events).
  onMouseenter: (ev, el) => {
    el.call('cancelSearchAppsClose')
    el.call('setDropdownInert', false)
  },
  onMouseleave: (ev, el) => el.call('scheduleSearchAppsClose'),

  // A SECONDARY press — the right button, or Control + the primary button
  // (the Mac context click) — never moves focus: the header search input
  // stays the one focus owner (9A), so the press cannot blur it and start the
  // search's close while a tile's own menu is opening. MEASURED LIVE (dev
  // 147be4dd4, 2026-09-29): the tile took focus on the right button's
  // mousedown, the input's blur timer closed the launcher 200 ms later, and a
  // contextmenu that came after that landed on the home grid underneath
  // (__tests__/launcherTileContextMenu.test.js). A primary press is left
  // alone: it still focuses, clicks and starts a tile drag.
  onMousedown: (e) => {
    if (e && (e.button === 2 || (e.button === 0 && e.ctrlKey))) e.preventDefault()
  },

  // iPad: the per-tile context menu (SearchAppTile.onContextmenu) is
  // unreachable by touch — arm the shared long-press shim at the panel root;
  // the synthetic contextmenu bubbles from the touched tile. Re-arms per
  // dropdown open (fresh node), idempotent per node, no-op on fine pointers.
  onRender: (el) => {
    el.call('armLongPress')
    el.scope.armIfOutside(el)
  },

  // THE FILTER ROW (9A — Nika: "add search"; the picks page: "one filter
  // field … the header field keeps focus while you type"). A MIRROR of the
  // header search input, which stays the ONE focus owner: it shows what is
  // typed there (or what typing does), and a press on it puts the caret back
  // in that input — it is never a second field with its own focus. Hidden
  // from assistive technology for the same reason: the header input is the
  // labelled control, and a duplicate would read the query twice.
  // The COMPANION's row only (`_launcherMode`, functions/searchApps.js): a
  // panel a trigger opened has no header field to mirror, and owns a real
  // one instead (SamFilterField below).
  SamFilter: {
    flow: 'x',
    align: 'center',
    gap: 'Z',
    flexShrink: '0',
    padding: 'Z A',
    round: 'radiusControl',
    theme: 'field',
    color: 'caption',
    cursor: 'text',
    'aria-hidden': 'true',
    hide: (el, s) => s.root._launcherMode === 'trigger',
    subscribeTo: ['navSearchQuery', '_navSearchChips'],
    // Mousedown, not click: the press must not take focus from the input it
    // hands the caret back to.
    onMousedown: (e, el) => {
      e.preventDefault()
      el.call('focusHeaderSearch')
    },
    Icon: { name: 'pxSearch', boxSize: 'iconSm', flexShrink: '0' },
    SamFilterText: {
      tag: 'span',
      flex: '1',
      minWidth: '0',
      overflowWrap: 'anywhere',
      fontSize: 'Z1',
      color: (el, s) =>
        (s.root.navSearchQuery || '').trim() || (s.root._navSearchChips || []).length
          ? 'title'
          : 'caption',
      // The typed text, after the active ⌘K chips (CMDK-05) when there are
      // any — the field reads what the results are filtered by.
      text: (el, s) =>
        [
          ...(s.root._navSearchChips || []).map(
            (c) => `${c.fieldLabel || c.field}: ${c.label || c.value}`
          ),
          (s.root.navSearchQuery || '').trim()
        ]
          .filter(Boolean)
          .join(' · ') || el.call('polyglot', 'shell.launcher.filterPlaceholder')
    }
  },

  // THE OWN FILTER — the TRIGGER mode's field (`_launcherMode`, functions/
  // searchApps.js; Nika 2026-10-28: "in more apps sidebar search is not
  // functioning" … "ok lets keep it for apps filtering, and main search will
  // also stay as is"). Opened from a navbar chevron or the home App strip,
  // the panel had only the mirror above, and the header field it mirrored
  // was not in use: a press moved the caret there and nothing filtered. This
  // is a real field the panel owns. Typing narrows the tiles in place
  // (SamSections, by the name in the current language) inside the chosen
  // category, and never writes `navSearchQuery` or runs the header search.
  // The house leading-icon field (shared AppInputWithIcon): one surface, one
  // edge, the brand ring on the group while the input is focus-visible.
  SamFilterField: {
    extends: 'AppInputWithIcon',
    icon: 'pxSearch',
    flexShrink: '0',
    hide: (el, s) => s.root._launcherMode !== 'trigger',
    AppInputBare: {
      type: 'text',
      autocomplete: 'off',
      spellcheck: 'false',
      // The keyboard model's handle: the stop armIfOutside focuses on open and
      // the first stop of tabCycle.
      dataLauncherFilter: true,
      placeholder: '{{ workspace.launcher.filterPlaceholder | polyglot }}',
      ariaLabel: '{{ workspace.launcher.filterPlaceholder | polyglot }}',
      // A filter is not unsaved work: the field kit's `dirty` would make a
      // pick from a typed filter ask "Discard changes?" (functions/
      // hasUnsavedInput.js walks the root, and this panel mounts under it).
      dirty: false,
      subscribeTo: ['_launcherFilter'],
      value: (el, s) => s.root._launcherFilter || '',
      onInput: (e, el, s) => {
        s.root.update({ _launcherFilter: e.target.value }, { preventFetch: true })
      },
      onKeydown: (e, el, s) => {
        // The caret keys belong to the text: without this the panel's grid
        // walk (menuArrowNav, both axes) took them to the tiles. The vertical
        // arrows, Tab and Escape go on to the panel's own keyboard model.
        if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) {
          e.stopPropagation()
          return
        }
        if (e.key !== 'Enter') return
        // Enter opens the first tile the filter keeps, the way a press on it
        // does (SearchAppTile onClick: stamp, close, open).
        e.preventDefault()
        if (!(s.root._launcherFilter || '').trim()) return
        const groups = el.call('launcherTileGroups', s.root)
        const first = groups.length ? groups[0].apps[0] : null
        if (!first || !first.route) return
        try {
          el.call('noteSearchAppsDismissed')
        } catch (_) {}
        el.call('closeDropdown', 'searchApps')
        el.call('selectAppFromSwitcher', first.route, {
          metaKey: !!e.metaKey,
          ctrlKey: !!e.ctrlKey
        })
      }
    }
  },

  // Header bar (2026-07-30 per Nika "improve navbar of search dropdown"):
  // "Apps" caption on the left; App Store (the renamed Marketplace entry —
  // its tile left the grid), Following (the community feed, where the follow
  // graph lives) and the primary New app action on the right.
  // The launcher's toolbar WRAPS (the overflow law, HOME_AMBIENT_SKIN_SPEC
  // §3b rule 5 — a toolbar wraps or overflows into a menu, nothing is cut at
  // the edge). Measured at 390 in ka (dev 2c2b6d978): the row needed 562 px
  // in a 326 px head, so "Following" was cut and "New app" sat 204 px past
  // the panel. Wrapped, the buttons take a second line under the title, at
  // the end edge; the title keeps the free width of the first.
  SamHeader: {
    flow: 'x',
    flexWrap: 'wrap',
    align: 'center',
    justifyContent: 'flex-end',
    gap: 'Z',
    flexShrink: '0',
    // The apps view's toolbar: typed text — or an active ⌘K chip (CMDK-05) —
    // turns the companion panel into the results (SamResults), where the
    // groups carry their own heads.
    subscribeTo: [
      'navSearchQuery',
      '_navSearchChips',
      '_launcherGroup',
      '_featureFlags',
      'workspaceSettings',
      'workspaceProject'
    ],
    hide: (el, s) =>
      s.root._launcherMode !== 'trigger' &&
      (!!(s.root.navSearchQuery || '').trim() || !!(s.root._navSearchChips || []).length),

    // The mono micro-label of the redesign (DESIGN_SPEC §5.7), the same
    // label every section head below wears.
    SamTitle: {
      extends: 'AppMicroLabel',
      flex: '1',
      // D1: in the wrapping row a `flex: 1` label with overflowWrap anywhere shrank to one letter beside
      // the capsules (the ka title as a tower at 390). On the phone step it takes its own row.
      '@mobileL': { flex: '1 0 100%' },
      paddingLeft: 'Z',
      text: '{{ shell.launcher.heading | polyglot }}'
    },

    AppStoreBtn: {
      extends: 'SamHeaderGhostBtn',
      title: (el) => el.call('polyglot', 'shell.launcher.appStoreTooltip'),
      'aria-label': (el) => el.call('polyglot', 'shell.launcher.appStore'),
      Icon: { extends: 'Icon', name: 'pxAppStore', boxSize: 'iconSm' },
      Label: { tag: 'span', text: '{{ shell.launcher.appStore | polyglot }}' },
      onClick: (e, el) => {
        e.preventDefault()
        e.stopPropagation()
        el.call('closeDropdown', 'searchApps')
        // Land on the App Store SURFACE, not the marketplace one — the
        // island's own deep-link (`?surface=appstore`, MarketplaceIsland
        // activeSurface) selects it (2026-08-06 per Nika "appstore button
        // click should go to appstore active - not marketplace").
        el.call('openAppPage', '/marketplace?surface=appstore')
      }
    },

    FollowingBtn: {
      extends: 'SamHeaderGhostBtn',
      title: (el) => el.call('polyglot', 'shell.launcher.followingTooltip'),
      'aria-label': (el) => el.call('polyglot', 'shell.launcher.following'),
      Icon: { extends: 'Icon', name: 'pxFollowing', boxSize: 'iconSm' },
      Label: { tag: 'span', text: '{{ shell.launcher.following | polyglot }}' },
      onClick: (e, el) => {
        e.preventDefault()
        e.stopPropagation()
        el.call('closeDropdown', 'searchApps')
        el.call('openAppPage', '/marketplace/feed')
      }
    },

    // The one primary of the panel — the INK capsule (DESIGN_SPEC §9: an
    // accent never fills a primary, §4.4), compact on the header row. It
    // joins the keyboard cycle with the ghost actions beside it (A2-06).
    NewAppBtn: {
      extends: 'AppCapsuleBtnPrimary',
      compact: true,
      role: 'menuitem',
      title: (el) => el.call('polyglot', 'shell.launcher.newAppTooltip'),
      'aria-label': (el) => el.call('polyglot', 'shell.launcher.newApp'),
      Icon: { extends: 'Icon', name: 'pxPlus', boxSize: 'iconSm' },
      Label: { tag: 'span', text: '{{ shell.launcher.newApp | polyglot }}' },
      onClick: (e, el) => {
        e.preventDefault()
        e.stopPropagation()
        el.call('closeDropdown', 'searchApps')
        el.call('openAppPage', '/add-app')
      }
    }
  },

  CategoryNav: {
    role: 'group',
    'aria-label': (el) => el.call('polyglot', 'shell.launcher.categories'),
    flow: 'x',
    gap: 'Y',
    flexShrink: '0',
    flexWrap: 'wrap',
    subscribeTo: [
      'navSearchQuery',
      '_navSearchChips',
      '_launcherGroup',
      'workspaceSettings',
      'workspaceProject',
      '_featureFlags'
    ],
    hide: (el, s) =>
      s.root._launcherMode !== 'trigger' &&
      (!!(s.root.navSearchQuery || '').trim() || !!(s.root._navSearchChips || []).length),
    childExtends: 'SearchAppCategory',
    childrenAs: 'state',
    children: (el, s) => [
      { key: 'all', label: 'shell.launcher.allApps' },
      ...searchAppGroupsFor(s.root).map((g) => ({ key: g.key, label: g.label }))
    ]
  },

  SamSections: {
    flow: 'y',
    // A cut row reads as "more below": the grid fades at its scroll edge (dropdowns spec §6). The fade spans
    // exactly the bottom padding (B), so at the end of the list it lies over empty padding and the last row
    // is never dimmed; only rows that continue below pass under it (design-d2 review of 45934c56d).
    // `black` is a brand colour, so the var always resolves (a missing var would void the gradient silently).
    maskImage:
      'linear-gradient(to bottom, var(--color-black) calc(100% - var(--spacing-B)), transparent)',
    paddingBottom: 'B',
    gap: 'B',
    // THE INNER SCROLLER of the apps view (9A "taller" + the toolbar stays).
    flex: '1 1 auto',
    minHeight: '0',
    overflow: 'auto',
    overscrollBehavior: 'contain',
    subscribeTo: [
      'navSearchQuery',
      '_navSearchChips',
      '_launcherGroup',
      '_launcherFilter',
      'workspaceSettings',
      'workspaceProject',
      '_featureFlags'
    ],
    hide: (el, s) =>
      s.root._launcherMode !== 'trigger' &&
      (!!(s.root.navSearchQuery || '').trim() || !!(s.root._navSearchChips || []).length),
    childExtends: 'SearchAppsSection',
    childrenAs: 'state',
    // Workspace-scoped: prepends the GreenIG section when the active workspace
    // runs the GreenIG module set (resolved at open time — the dropdown mounts
    // fresh each open with root.workspaceSettings already hydrated). The
    // chosen category's groups, narrowed by the trigger mode's own filter; a
    // section the filter empties is dropped (functions/searchApps.js
    // launcherTileGroups).
    children: (el, s) => el.call('launcherTileGroups', s.root)
  },

  // The trigger mode's quiet line when the typed filter keeps no tile: the
  // sections are gone, and an empty panel would read as broken. It wears the
  // header search's own empty row (NavSearchNoResults), which the companion
  // shows in this same panel. Its own key: the glossary's "No matches" is
  // შესატყვისი არ არის, and the common one reads otherwise in ka.
  SamNoMatch: {
    padding: 'Y1 A',
    fontSize: 'Z2',
    color: 'caption',
    role: 'status',
    subscribeTo: [
      '_launcherFilter',
      '_launcherGroup',
      'workspaceSettings',
      'workspaceProject',
      '_featureFlags'
    ],
    hide: (el, s) =>
      s.root._launcherMode !== 'trigger' ||
      !(s.root._launcherFilter || '').trim() ||
      el.call('launcherTileGroups', s.root).length > 0,
    text: '{{ workspace.launcher.noMatches | polyglot }}'
  },

  // THE RESULTS, in the same panel (9A — the ONE search dropdown): with text
  // in the header search the launcher shows what globalSearch found — the
  // same groups and rows the header's own panel renders (NavSearchResultGroup
  // and its three row kinds), keyboard highlight included — instead of
  // closing. The header search keeps the panel open while typing (the shared
  // HeaderSearch half, design-d2); until that half lands, typing closes the
  // launcher exactly as before and this block never shows. The companion's
  // view only: a trigger-opened panel keeps its tiles whatever the header
  // holds (`_launcherMode`).
  SamResults: {
    flow: 'y',
    flex: '1 1 auto',
    minHeight: '0',
    overflow: 'auto',
    overscrollBehavior: 'contain',
    subscribeTo: ['navSearchQuery', '_navSearchChips'],
    hide: (el, s) =>
      s.root._launcherMode === 'trigger' ||
      (!(s.root.navSearchQuery || '').trim() && !(s.root._navSearchChips || []).length),
    NavSearchLoading: {},
    NavSearchNoResults: {},
    SamResultGroups: {
      flow: 'y',
      subscribeTo: ['_navSearchGrouped', '_navSearchLoading'],
      if: (el, s) => !!(s.root._navSearchGrouped && s.root._navSearchGrouped.length),
      // The stale rows dim while the next query fetches — the header panel's
      // own rule, so a row never masquerades as the answer to the new text.
      opacity: (el, s) => (s.root._navSearchLoading ? '0.45' : '1'),
      transition: 'opacity A defaultBezier',
      children: (el, s) => s.root._navSearchGrouped || [],
      childExtends: 'NavSearchResultGroup',
      childrenAs: 'state'
    }
  }
}

// Ghost header action — the quiet sibling of the ink NewAppBtn, shared by
// App Store + Following: the kit's compact ghost capsule (no fill at rest,
// the house washes under the pointer), caption ink stepping to title.
export const SamHeaderGhostBtn = {
  extends: 'AppCapsuleBtnGhost',
  compact: true,
  // A2-06 — the header actions join the same keyboard cycle as the tiles.
  role: 'menuitem',
  // THE STATE LOGIC (dropdowns spec §5): a hairline capsule at rest; under the pointer and the keyboard the
  // capsule INVERTS (`highlight` fill, `title-reversed` ink) — the same answer the tiles give.
  borderWidth: 'hairline',
  borderStyle: 'solid',
  borderColor: 'line',
  color: 'title',
  ':hover': { background: 'highlight', color: 'title-reversed', borderColor: 'highlight' },
  ':focus-visible': {
    background: 'highlight',
    color: 'title-reversed',
    borderColor: 'highlight',
    shadow: 'focusRing'
  }
}

// One category section — the mono micro-label head + tile grid.
export const SearchAppsSection = {
  flow: 'y',
  gap: 'Z1',

  SasHead: {
    extends: 'AppMicroLabel',
    paddingLeft: 'Z',
    // i18n — the group head is DATA (searchAppGroupsFor), and it arrives from
    // two kinds of source at once: shipped house groups, which carry polyglot
    // KEYS, and the workspace's own group, whose label is the customer's
    // workspace NAME. Both go through the one call: `translate()` resolves a
    // shipped key and returns anything else unchanged, so a tenant's name is
    // never mangled by a lookup that was never meant for it.
    text: (el, s) => el.call('polyglot', s.label || '')
  },

  SasGrid: {
    display: 'grid',
    // ONE column grid for every section, so every tile aligns to the same
    // column across sections (2026-06-14 per Nika "fix layout in
    // SamSections" — `auto-FILL` keeps the empty tracks of a short section,
    // never an `auto-fit` stretch).
    //
    // THE TRACK FLOOR IS THE LONGEST NAME (the overflow law, HOME_AMBIENT_
    // SKIN_SPEC §3b; CLIP_AUDIT_2026-09-26 #2/#3, the launcher twin). The
    // fixed six columns left a 64 px label at 1440 (measured on dev
    // 2c2b6d978, ka): twelve names were cut to an ellipsis, and with the
    // ellipsis gone they broke mid-word ("პროდუქტებ / ი") and ran to three
    // lines. The floor is `E3` (151.5 px at the 16 px panel, measured): with
    // the tile's `Y` side padding it leaves a 139 px label, where the longest
    // shipped word ("ადმინისტრირების", "ინფრასტრუქტურა") measures 134 px at
    // the label's `Z` — so no house name breaks inside a word and every one
    // fits two lines. Both are em-based, so the ratio holds at every root.
    // The `max()` caps the grid at six columns on a wide panel (1/6 of the
    // row), so a 2560 screen keeps today's six; a 1440 panel shows four, a
    // phone two. The breakpoint overrides this replaces are gone.
    gridTemplateColumns:
      'repeat(auto-fill, minmax(max(var(--spacing-E3), calc((100% - 5 * var(--spacing-Z)) / 6)), 1fr))',
    justifyItems: 'center',
    gap: 'Z',
    childExtends: 'SearchAppTile',
    childrenAs: 'state',
    children: (el, s) => s.apps || []
  }
}

// One springboard tile: the calm glyph plate + label below, centered.
export const SearchAppTile = {
  extends: 'AppControl',
  dataLauncherTile: true,
  tag: 'button',
  type: 'button',
  // A2-06 — the row marker the house menu walker collects.
  role: 'menuitem',
  flow: 'y',
  align: 'center center',
  gap: 'Z',
  // `Y` at the sides (it was `Z`): the name gets the track's width less a
  // thin inset — the floor above is measured against it.
  padding: 'A Y',

  // A NAME LONGER THAN TWO LINES GETS THE ROOM (the widget law, HOME_AMBIENT_
  // SKIN_SPEC §3: primary text wraps to at most two lines, or the layout gives
  // it room; CLIP_AUDIT round 3 (D)). One track at the grid's floor holds two
  // lines of about 28 characters: the name box is 139px (E3 less the Y
  // sides) and a Georgian glyph averages 9.1px at the name's Z (measured:
  // "ადმინისტრირების", 15 characters, 136px); every shipped name fits, the
  // longest being 25 ("სამუშაო სივრცის მოხმარება"). A customer's longer name
  // spans two tracks — a name box of at least 300px, where the measured
  // round-3 string (38 characters, 333px on one line) breaks as it did live
  // at 2560, "ამ თვის გაყიდვები — დეტალური" / "მიმოხილვა" — and a name past
  // twice that takes the whole row. Keyed on the RESOLVED name (the store's
  // word for an installed app), counted in characters, so the span never
  // depends on a font having loaded. Static rules on the attribute, never a
  // factory on `gridColumn`.
  dataSpan: (el, s) => {
    const name = String(el.call('storeItemText', { key: s.appKey, name: s.title }, 'name') || '')
    const n = [...name].length
    return n > 56 ? 'row' : n > 28 ? '2' : null
  },
  '&[data-span="2"]': { gridColumn: 'span 2' },
  '&[data-span="row"]': { gridColumn: '1 / -1' },
  // Long custom-app names used to run sideways over the neighbouring tile's
  // label ("This Week's Revenue (Jul 27 – Aug 2)" rendered ~275px wide in a
  // 106px grid track, colliding with "Jobs").
  //
  // `min-width: 0` alone does NOT fix it here: this tile is a <button>, and
  // Chrome keeps a button's internal layout at its min-content width
  // regardless of min-width, so the item still overflowed its `minmax(0, 1fr)`
  // track. `max-width: 100%` clamps against the track itself, which the button
  // does honour; min-width 0 stays as the standard grid-item companion.
  //
  // No `overflow: hidden` since the overflow law (HOME_AMBIENT_SKIN_SPEC §3b
  // rules 1 and 8): the name is the tile's PRIMARY text, so it WRAPS inside
  // the track (Label below) instead of being cut to an ellipsis, and a clip
  // on the tile would only cut what the wrap already fits.
  minWidth: '0',
  maxWidth: '100%',
  round: 'radiusCard',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  // Drag-to-navbar pin source (2026-08-06 per Nika "I should be able to
  // drag and drop items from searchDropwon to navbar"): a native drag
  // stamped with the house entity-ref wire format — HeaderTabs' strip
  // accepts it as a PIN at the pointer position; the chat composer and the
  // AI prompt accept the same drag as a page reference for free (one MIME,
  // shared/functions/dragRef.js). Supported Home shortcuts add one narrow
  // MIME alongside that ref, so the SAME drag can copy an app onto Home
  // without changing any existing navbar/drop-target behavior.
  draggable: 'true',
  onDragstart: (e, el, s) => {
    // i18n — the ref's `title` is stamped RESOLVED, never as the key, because
    // this ref leaves the launcher and gets WRITTEN somewhere permanent: the
    // chat composer and the AI prompt serialize it into a message body
    // (`dragRefToText` → `[title](href)`), and HeaderTabs turns it into a
    // custom navbar pin stored in userPrefs. A key in a sent message is
    // garbage nobody can clean up. Words are the right payload for a wire
    // format read by humans; the tile's own DISPLAY still runs off the key.
    // Resolved through the SAME store-copy resolver the tile label uses, so a
    // pin or a chat mention of an installed app carries the app's real name
    // ("ინვენტარი"), never its manifest's English literal ("Warehouse").
    el.call('setDragRef', e, {
      kind: 'page',
      id: s.route || '',
      title: el.call('storeItemText', { key: s.appKey, name: s.title }, 'name'),
      href: s.route || '',
      meta: { navPin: true, icon: s.icon || '' }
    })
    el.call('stampLauncherDashboardDrag', e, s.route)
  },
  // Belt-and-suspenders for CODEX-V8 row 5 (tickets/sonnet.md 2026-08-13):
  // live QA instrumented document-level capture listeners and proved a
  // clean, correctly-payloaded native `drop` can land squarely on
  // GridContainer (the element GridContainer.onDrop is declared on) and
  // still produce zero observable effect — no state flip, no toast, no
  // console error. A scratch, isolated regression harness against
  // `@symbo.ls/element`'s own `registerEvents`/delegate.js (create an
  // element with byte-identical onDragover/onDrop handlers, dispatch a
  // dragover+drop with `e.target === node` and a fake DataTransfer) fired
  // both handlers correctly — so this isn't a delegation defect
  // reproducible outside a real trusted-drag browser session; the gap is
  // environment/timing specific and wasn't independently re-creatable
  // without live Chrome access. `dropEffect !== 'none'` means the browser's
  // own DnD state machine confirms SOME target genuinely accepted the drop
  // (GridContainer is the only element on Home that ever calls
  // preventDefault for this MIME); if the widget still isn't showing
  // afterward, GridContainer's own onDrop silently no-oped. Replay through
  // the independently keyboard-verified `addLauncherAppToDashboard` path
  // instead of leaving the user's drag with no visible result. Idempotent +
  // no duplicate toast: `isHomeWidgetVisibleOnGrid` reads the LIVE grid DOM
  // (not the `_homeHiddenWidgets` map, which a successful "already visible"
  // onDrop branch never touches), so a drop that DID work is correctly
  // skipped.
  onDragend: (e, el, s) => {
    const widgetId = el.call('dashboardWidgetForRoute', s.route)
    if (!widgetId) return
    if (!e.dataTransfer || e.dataTransfer.dropEffect === 'none') return
    if (el.call('isHomeWidgetVisibleOnGrid', widgetId)) return // the drop already worked
    // markPageSaved: opt-out — a dropped tile adds a home widget at once; the panel's one kit field is the apps filter (dirty: false)
    el.call('addLauncherAppToDashboard', s.route)
  },
  color: 'title',
  // Hover is the wash, nothing moves; the press is the card press (spec §6,
  // overflow law rule 7 — a tile that lifted and grew 3 % was cut by the
  // menu's own scroll edge).
  transition: 'background-color B defaultBezier, transform Z defaultBezier',
  ':hover': { background: 'hover' },
  ':active': { transform: 'scale(0.98)' },
  // THE STATE LOGIC (dropdowns spec §5): hover, keyboard focus and the app you are in INVERT the glyph plate;
  // the app you are in also shows the orange dot. Declared here, on the holder, through the plate's hook.
  ':focus-visible': { shadow: 'focusRing' },
  '&:hover [data-glyph-plate]': {
    background: 'highlight',
    color: 'title-reversed',
    borderColor: 'highlight'
  },
  '&:focus-visible [data-glyph-plate]': {
    background: 'highlight',
    color: 'title-reversed',
    borderColor: 'highlight'
  },
  '&[aria-current="page"] [data-glyph-plate]': {
    background: 'highlight',
    color: 'title-reversed',
    borderColor: 'highlight'
  },
  '&[aria-current="page"] [data-glyph-dot]': { display: 'block' },
  // the app you are in (shared/functions/glyphNames.js appRouteIsCurrent — the App strip asks the same)
  'aria-current': (el, s) => (el.call('appRouteIsCurrent', s.route, s.root) ? 'page' : null),
  // Both take the FUNCTION form: each composes the tile's own data (caption,
  // store name, route) with a bundle key, which no static template can. (A
  // static `{{ … | polyglot }}` on an attribute switches language only from
  // element 3.14.788; before it, it rendered empty — I18N-CONVENTION.md §1.1.)
  // The hint is a SEPARATE key appended after the label rather than an
  // English sentence with the label interpolated into it — §1.3.
  // The caption keeps its preference here — it is the tile's own SUBTITLE, a
  // different string from the store's long blurb, and swapping one for the
  // other would change what the tooltip says, not just its language. Only the
  // FALLBACK changed: a tile with no caption now names the app through the
  // store-copy resolver instead of printing its manifest literal.
  title: (el, s) => {
    const label = s.caption
      ? el.call('polyglot', s.caption)
      : el.call('storeItemText', { key: s.appKey, name: s.title }, 'name')
    if (!el.call('dashboardWidgetForRoute', s.route)) return label
    return `${label} — ${el.call('polyglot', 'shell.launcher.dragToHomeHint')}`
  },
  'aria-label': (el, s) => {
    const label = el.call('storeItemText', { key: s.appKey, name: s.title }, 'name')
    if (!el.call('dashboardWidgetForRoute', s.route)) return label
    return `${label}. ${el.call('polyglot', 'shell.launcher.dragToHomeAria')}`
  },
  'aria-keyshortcuts': 'Alt+Enter',

  // Native drag has no keyboard equivalent. Alt+Enter uses the same launcher
  // intent as the context-menu action; unsupported routes remain ordinary app
  // buttons and never manufacture a dashboard widget.
  onKeydown: (e, el, s) => {
    if (!e.altKey || e.key !== 'Enter') return
    if (!el.call('dashboardWidgetForRoute', s.route)) return
    e.preventDefault()
    e.stopPropagation()
    // markPageSaved: opt-out — Alt+Enter adds a home widget at once; the panel's one kit field is the apps filter (dirty: false)
    el.call('addLauncherAppToDashboard', s.route)
  },

  // Warm the app's chunk while the pointer rests on the tile or keyboard focus
  // reaches it (functions/slotPrefetch.js) — the pick then opens at once.
  onPointerenter: (e, el, s) => el.call('prefetchSlotRoute', s.route),
  onFocus: (e, el, s) => el.call('prefetchSlotRoute', s.route),

  onClick: (e, el, s) => {
    if (!s.route) return
    // Stamp before the close, same reason as SearchAppsMenu.scope.dismiss:
    // the close hands focus back to the search input, whose onFocus would
    // re-open the launcher over the app the user just picked.
    try {
      el.call('noteSearchAppsDismissed')
    } catch (_) {}
    el.call('closeDropdown', 'searchApps')
    // Save the current Simone thread + open chat history, then open the app
    // (2026-06-14 per Nika). See functions/searchApps.js selectAppFromSwitcher.
    // Keep the same split gesture as navbar links. Pass only the intent,
    // not the native event; the route opener owns pane targeting and width.
    el.call('selectAppFromSwitcher', s.route, {
      metaKey: !!e.metaKey,
      ctrlKey: !!e.ctrlKey
    })
  },

  // Right-click → Open / Open in side view / Add to dashboard (rows + dispatch
  // in functions/searchAppTileMenu.js — same ContextDropdown contract as the
  // navbar tab menu). 2026-07-30 per Nika.
  onContextmenu: (e, el, s) => {
    if (!s.route) return
    e.preventDefault()
    e.stopPropagation()
    s.root.update(
      {
        contextDropdown: {
          open: true,
          x: e.clientX,
          y: e.clientY,
          itemsFn: 'searchAppTileMenuItems',
          actionFn: 'searchAppTileMenuAction',
          // The launcher is the search's one dropdown (9A), so its tile menu
          // belongs to the search exactly like a result row's menu
          // (NavSearchDropdown.js): the header's blur guard keeps the search
          // open while it is up, and scheduleSearchAppsClose (searchApps.js)
          // keeps the launcher behind it.
          fromNavSearch: true,
          // `projectId`/`kind` ride along so the menu can offer "Edit" on a
          // custom, AI-built app (workspaceProjects.js stamps them onto
          // extension tiles). House tiles carry neither and get no Edit row.
          //
          // i18n — RESOLVED title, for the same reason the drag ref stamps
          // resolved words: the menu's "Pin to navbar" row writes this string
          // into the user's own prefs as a custom pin label, and a pin is
          // durable. A house tile pinned from a Georgian session should read
          // Georgian, not `shell.launcher.studio.title`.
          appTile: {
            title: el.call('storeItemText', { key: s.appKey, name: s.title }, 'name'),
            icon: s.icon,
            iconBg: s.iconBg,
            route: s.route,
            projectId: s.projectId,
            kind: s.kind
          }
        }
      },
      { preventFetch: true }
    )
  },

  // The CALM glyph (9A — "calm glyph tiles instead of bright gradients"):
  // THE app glyph plate the home App strip wears (components/AppGlyphTile.js)
  // — neutral, title ink — so an app reads the same in the strip and here.
  IconTile: {
    extends: 'AppGlyphPlate'
  },

  // The app's NAME — primary text, never cut: it wraps under the glyph.
  // A shipped name never needs more than two lines at the grid's floor (see
  // SasGrid); `anywhere` is the safety net for a customer's own name with a
  // word longer than any track.
  Label: {
    tag: 'span',
    fontSize: 'Z',
    fontWeight: '500',
    color: 'title',
    textAlign: 'center',
    overflowWrap: 'anywhere',
    width: '100%',
    // i18n — resolved through THE store-copy resolver, which is the same
    // function the App Store and the navbar tabs use, so one app has one name
    // everywhere. Its source order carries all three kinds of tile with no
    // branch here (see shared/functions/storeItemText.js):
    //   • an INSTALLED APP's tile carries `appKey` and resolves to the name
    //     the store shows for that record — it printed the app manifest's
    //     English literal before ("Warehouse" against the store's "ინვენტარი");
    //   • a HOUSE tile's title is a polyglot key (the VIEWS tiles come from
    //     CanvasViewNav's VIEW_NAV, and this site NEEDED that: it was printing
    //     `shell.viewNav.apps` / `.infra` / `.agents` on screen) and resolves
    //     at the resolver's last source, which IS polyglot;
    //   • an EXTENSION's or a customer's own title resolves nothing anywhere
    //     and passes through unchanged.
    text: (el, s) => el.call('storeItemText', { key: s.appKey, name: s.title }, 'name')
  }
}

// Category choice only filters navigation. It never changes the current app.
export const SearchAppCategory = {
  role: 'menuitemradio',
  extends: 'AppCapsuleBtnToolbar',
  compact: true,
  dataLauncherCategory: true,
  subscribeTo: ['_launcherGroup'],
  background: 'widgetPlate',
  color: 'caption',
  isActive: (el, s) => (s.root._launcherGroup || 'all') === s.key,
  '.isActive': { background: 'highlight', color: 'highlight-reversed' },
  'aria-checked': (el, s) => ((s.root._launcherGroup || 'all') === s.key ? 'true' : 'false'),
  Text: { text: (el, s) => el.call('polyglot', s.label) },
  onClick: (e, el, s) => {
    e.preventDefault()
    e.stopPropagation()
    s.root.update({ _launcherGroup: s.key }, { preventFetch: true })
  }
}
